import {
  EMPTY_ARR,
  EMPTY_OBJ,
  NOOP
} from '@dadohr/shared/domainTypes/emptyConstants';
import { getItemDisplayName } from '../../../helpers/adminName';
import {
  Audience,
  AutomationActionDenormalized,
  BatchMode,
  ContentTemplate,
  ContentType,
  IExperienceMessageDenormalized,
  IItemTemplateTimingTriggeredTask,
  isContentBlock,
  isLayoutBlock,
  isSubTaskBlock,
  ITaskDenormalized,
  TaskBlockDenormalized
} from '@dadohr/shared/domainTypes/experience';
import {
  ExperiencePreviewMode,
  PreviewBatch,
  PreviewGroup,
  PreviewGroupType,
  PreviewItem,
  PreviewItemAutomation,
  PreviewItemMessage,
  PreviewItemTask,
  PreviewItemType
} from '@dadohr/shared/domainTypes/experiencePreview';
import { Markdown } from '@dadohr/shared/domainTypes/markdown';
import {
  CustomUserField,
  IOrganizationDenormalized
} from '@dadohr/shared/domainTypes/org';
import { ScheduleChannel } from '@dadohr/shared/domainTypes/schedules';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Avatar,
  Dialog,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography
} from '@material-ui/core';
import { capitalize } from 'lodash';
import {
  Calendar,
  CheckCircle2,
  Eye,
  Flag,
  LucideIcon as Icon,
  Mail,
  Rocket,
  Slash,
  Zap
} from 'lucide-react';
import { Moment } from 'moment';
import { createElement, useMemo, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
// @ts-ignore
import Truncate from 'react-truncate';
import { WithShape } from '../../../../../components/Charts/Util';
import { TimelineIcon } from '../../../../../components/CustomIcons';
import { DialogActionsHeader } from '../../../../../components/DialogActionsHeader';
import { DialogContentWithoutPadding } from '../../../../../components/DialogContentWithoutPadding';
import {
  FilterByActorChip,
  FilterByItemTypeChip
} from '../../../../../components/Filters';
import { IconContainer } from '../../../../../components/IconContainer';
import {
  MarkdownText,
  PlainTextWithReplacementPlaceholders,
  TokenDefinition
} from '../../../../../components/MarkdownText';
import { SubTaskAsText } from '../../../../../components/SubTask';
import { SubTaskIcon } from '../../../../../components/SubTaskIcon';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../../i18next';
import { FlexContainer } from '../../../../../layout/Flex';
import {
  channelToKey,
  channelToString
} from '../../../../../services/channels';
import { isHexColor } from '../../../../../services/color';
import {
  getAudienceName,
  getEveryoneAudience
} from '../../../../../services/experiences/audience';
import {
  escapeReplacements,
  LightMdToJsx
} from '../../../../../services/formatter';
import {
  getMomentInTz,
  toMoment,
  toWeekOrDayDuration
} from '../../../../../services/time';
import { useUserRuleConfigSimple } from '../../../../../services/user';
import { ActorDescriptor } from '../../../components/Actors';
import { AnchorField } from '../../../components/Anchors';
import { ActionSummary } from '../../../components/Builders/AutomationBuilder';
import { useActionDefinitions } from '../../../components/Builders/AutomationBuilder/actionHelpers';
import { ActionDefinition } from '../../../components/Builders/AutomationBuilder/types';
import {
  AudienceBlockContainer,
  AudienceBlockTitle,
  DEFAULT_AUDIENCE_COLOR
} from '../../../components/Builders/TaskBuilder/components/Audiences';

import {
  MAIN_CONTAINER_MIN_WIDTH,
  MainContainer,
  SettingsBody,
  SIDE_CONTAINER_MAX_WIDTH,
  SIDE_CONTAINER_MIN_WIDTH
} from '../../../components/Builders/TaskBuilder/components/layout';
import {
  ExperienceMessagePreviewBody,
  TaskMessagePreviewStart
} from '../../../components/Builders/TaskBuilder/components/Messages';
import { MinimalTaskBuilderContext } from '../../../components/Builders/TaskBuilder/types';
import { ChannelIcon } from '../../../components/Channels';
import { RuleRenderer } from '../../../components/RuleBuilder';
import {
  ActionWithIcon,
  BlockElementSummaryContainer
} from '../ParticipantDialog';
import { AudiencesByBlock, PreviewFilterState, PreviewVariant } from './types';
import { previewBatchToKey } from './utils';

type ActorDescriptors = { [actorKey: string]: ActorDescriptor };

const getAnchorIntervalText = (
  t: UnsafeTFunction,
  intervalData: IntervalData
) => {
  if (
    !intervalData.anchorName &&
    intervalData.groupType === PreviewGroupType.START
  ) {
    return 'On Experience start date';
  }

  if (intervalData.groupType === PreviewGroupType.ANCHOR) {
    return capitalize(intervalData.anchorName || '');
  }

  const isBefore = intervalData.intervalToAnchorInDays < 0;
  const isFullWeek = Math.abs(intervalData.intervalToAnchorInDays) % 7 === 0;
  const interval =
    Math.abs(intervalData.intervalToAnchorInDays) / (isFullWeek ? 7 : 1);
  const isSg = interval === 1;
  const text = t(
    `${interval} ${
      isSg ? (isFullWeek ? 'week' : 'day') : isFullWeek ? 'weeks' : 'days'
    } ${isBefore ? 'before' : 'after'} ${
      intervalData.anchorName || 'Experience start'
    }`
  );
  return text;
};

const ColoredContainerSquare = styled('div')<{
  color: string;
  size: number;
  gap?: number;
}>((p) => {
  const backgroundColor = isHexColor(p.color) ? `${p.color}65` : p.color;
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: p.theme.custom.border.radius(),
    backgroundColor,
    color: p.theme.custom.colors.gray.w700,
    gap: p.gap ? p.theme.spacing(p.gap) : 0,
    height: p.size,
    width: p.size,
    minWidth: p.size
  };
});

const ColoredContainer = styled('div')<{
  color: string;
  gap?: number;
}>((p) => {
  const backgroundColor = isHexColor(p.color) ? `${p.color}65` : p.color;
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${p.theme.spacing(0.5)}px ${p.theme.spacing(1)}px`,
    borderRadius: p.theme.custom.border.radius(),
    backgroundColor,
    color: p.theme.custom.colors.gray.w700,
    overflow: 'hidden',
    height: '100%',
    gap: p.gap ? p.theme.spacing(p.gap) : 0
  };
});

const audiencesToAudiencesByBlock = (as: Audience[]) => {
  const res: AudiencesByBlock = {};
  as.forEach((a) => {
    a.blocks.forEach((b) => {
      const container = (res[b] = res[b] || []);
      container.push(a);
    });
  });
  return res;
};

const EllipsisContainer = styled('div')((p) => ({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  maxWidth: 300
}));

const AudiencesHintContainer = styled('div')((p) => ({
  display: 'grid',
  alignItems: 'center',
  gridTemplateColumns: 'min-content 1fr',
  gap: p.theme.spacing(0.5),
  color: p.theme.custom.colors.gray.w700,
  backgroundColor: p.theme.custom.colors.yellow.w200,
  padding: `${p.theme.spacing(0.5)}px ${p.theme.spacing(1)}px`,
  borderRadius: p.theme.custom.border.radius()
}));
const AudiencesHint = ({ audiences }: { audiences: Audience[] }) => {
  const { t } = useUnsafeTranslation();

  const { text } = useMemo(() => {
    if (!audiences.length) {
      return { text: null };
    }

    // if more than 1, then collapse to say X audiences
    // otherwise display it
    if (audiences.length > 2) {
      return {
        text: t('Only for **{{ count }} Audiences**', {
          count: audiences.length
        })
      };
    }
    const s = audiences.map((a) => getAudienceName(a, true, t)).join(', ');
    if (s.length > 32) {
      return {
        text:
          audiences.length === 1
            ? t('Only for **1 Audience**')
            : t('Only for **{{ count }} Audiences**', {
                count: audiences.length
              })
      };
    } else {
      return {
        text: t('Only for **{{ audiences }}**', {
          audiences: audiences
            .map((a) => getAudienceName(a, true, t))
            .join(', ')
        })
      };
    }
  }, [audiences, t]);

  if (!text) {
    return null;
  }

  return (
    <Typography variant="caption">
      <Tooltip
        title={
          <div>
            {audiences.map((a) => (
              <div key={a.id}>{getAudienceName(a, true, t)}</div>
            ))}
          </div>
        }
      >
        <AudiencesHintContainer>
          <Eye size={16} />
          <EllipsisContainer>
            <LightMdToJsx>{text}</LightMdToJsx>
          </EllipsisContainer>
        </AudiencesHintContainer>
      </Tooltip>
    </Typography>
  );
};

const TruncatedMarkdownTextContainer = styled('div')((p) => ({
  width: '100%'
}));
const TypographyFullWidth = styled(Typography)((p) => ({
  width: '100%'
}));
const TruncatedMarkdownText = ({ t }: { t: Markdown }) => (
  <TypographyFullWidth variant="caption">
    <TruncatedMarkdownTextContainer>
      <Truncate>
        <MarkdownText t={t} />
      </Truncate>
    </TruncatedMarkdownTextContainer>
  </TypographyFullWidth>
);

const ContentBlockSummary = ({ b }: { b: ContentTemplate }) => {
  if (b.type === ContentType.TEXT || b.type === ContentType.CALLOUT) {
    return <TruncatedMarkdownText t={b.data.t} />;
  }

  if (b.type === ContentType.HEADING) {
    return <TruncatedMarkdownText t={{ t: b.data.t, v: 1 }} />;
  }

  // if (b.type === LayoutBlockType.COLUMNS) {
  //   return (
  //     <>
  //       {b.data.columns.filter(isValidColumnContent).map((c) => (
  //         <ContentBlockSummary b={c} key={c.id} />
  //       ))}
  //     </>
  //   );
  // }

  return null;
};

const PreviewItemElementsContainer = styled(FlexContainer)((p) => ({
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  flexDirection: 'column',
  width: '100%'
}));

const ELLIPSIS_STYLES: React.CSSProperties = {
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis'
};

const PreviewItemBlocksComponent = ({
  blocks,
  audiencesByBlock,
  audienceIdsOnItemLevel,
  allAudiences,
  mode
}: {
  blocks: TaskBlockDenormalized[];
  audiencesByBlock: AudiencesByBlock;
  audienceIdsOnItemLevel: Set<string>;
  allAudiences: Audience[];
  mode: ExperiencePreviewMode;
}) => {
  return (
    <PreviewItemElementsContainer>
      {blocks.map((b) => {
        if (isLayoutBlock(b)) {
          return null; // TODO?
        }

        if (isContentBlock(b)) {
          return <ContentBlockSummary key={b.id} b={b} />;
        }

        const audiences = audiencesByBlock[b.templateId] || [];
        if (!audiences.length) {
          return null;
        }
        const hideAudiences =
          (audiences.every((a) => audienceIdsOnItemLevel.has(a.id)) &&
            audiences.length === audienceIdsOnItemLevel.size) ||
          audiences.length === allAudiences.length;

        return (
          <FlexContainer
            justifyContent="flex-start"
            key={b.id}
            style={ELLIPSIS_STYLES}
          >
            <Typography variant="caption">
              <BlockElementSummaryContainer mode="light">
                <IconContainer>
                  <SubTaskIcon s={b} size={14} />
                </IconContainer>
                <SubTaskAsText s={b} />
              </BlockElementSummaryContainer>
            </Typography>
            {mode === ExperiencePreviewMode.EXPERIENCE && (
              <AudiencesHint
                audiences={hideAudiences ? EMPTY_ARR : audiences}
              />
            )}
          </FlexContainer>
        );
      })}
    </PreviewItemElementsContainer>
  );
};

const PreviewItemActionsComponent = ({
  actions,
  audiencesByBlock,
  audienceIdsOnItemLevel,
  allAudiences,
  mode
}: {
  actions: AutomationActionDenormalized[];
  audiencesByBlock: AudiencesByBlock;
  audienceIdsOnItemLevel: Set<string>;
  allAudiences: Audience[];
  mode: ExperiencePreviewMode;
}) => {
  return (
    <PreviewItemElementsContainer>
      {actions.map((a) => {
        const audiences = audiencesByBlock[a.templateId] || [];
        if (!audiences.length) {
          return null;
        }
        const hideAudiences =
          (audiences.every((a) => audienceIdsOnItemLevel.has(a.id)) &&
            audiences.length === audienceIdsOnItemLevel.size) ||
          audiences.length === allAudiences.length;
        return (
          <FlexContainer justifyContent="flex-start" key={a.templateId}>
            <ActionWithIcon mode="light" small action={a} />
            {mode === ExperiencePreviewMode.EXPERIENCE && (
              <AudiencesHint
                audiences={hideAudiences ? EMPTY_ARR : audiences}
              />
            )}
          </FlexContainer>
        );
      })}
    </PreviewItemElementsContainer>
  );
};

export const PreviewItemComponentTitle = ({
  icon,
  color,
  title,
  variant,
  audiences,
  onClick,
  mode
}: {
  icon: Icon;
  color: string;
  title: string;
  variant: PreviewVariant;
  audiences: Audience[];
  onClick?: () => void;
  mode: ExperiencePreviewMode;
}) => {
  return (
    <FlexContainer
      justifyContent="space-between"
      fullWidth
      role="button"
      onClick={onClick}
    >
      <FlexContainer>
        <ColoredContainerSquare color={color} size={26}>
          {createElement(icon, {
            size: 14
          })}
        </ColoredContainerSquare>
        {variant === 'minimal' && <Typography>{title}</Typography>}
        {variant === 'normal' && (
          <Typography>
            <strong>{title}</strong>
          </Typography>
        )}
      </FlexContainer>
      {mode === ExperiencePreviewMode.EXPERIENCE && (
        <AudiencesHint audiences={audiences} />
      )}
    </FlexContainer>
  );
};

const getItemIcon = (item: PreviewItem): Icon =>
  item.type === PreviewItemType.TASK
    ? CheckCircle2
    : item.type === PreviewItemType.MESSAGE
    ? Mail
    : PreviewItemType.AUTOMATION
    ? Zap
    : Rocket; // never used

const getItemTitle = (item: PreviewItem): string =>
  getItemDisplayName(item.item);

const PreviewItemDialogBody = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: `minmax(${SIDE_CONTAINER_MIN_WIDTH}px, ${SIDE_CONTAINER_MAX_WIDTH}px) minmax(${MAIN_CONTAINER_MIN_WIDTH}px, 1fr)`,
  gridColumnGap: p.theme.spacing(3),
  gridTemplateRows: '100%',
  height: '100%',
  width: '100%'
}));

const ActionSummaryContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(3),
  background: p.theme.custom.colors.background.light
}));

const EmptyStateContainer = styled(FlexContainer)`
  flex-direction: column;
  align-items: center;
  height: 100%;
  justify-content: center;
  color: ${(p) => p.theme.custom.colors.gray.w400};
`;

const TypographyGray = styled(Typography)`
  color: ${(p) => p.theme.custom.colors.gray.w500};
`;

const EmptyContent = ({ type }: { type: PreviewItemType }) => {
  const { t } = useUnsafeTranslation();

  return (
    <EmptyStateContainer spacing={3}>
      <Slash size={120} />
      <TypographyGray variant="h4">
        {type === PreviewItemType.MESSAGE &&
          t(`This message won't be sent for this audience`)}
        {type === PreviewItemType.TASK &&
          t(`This task won't be sent for this audience`)}
        {type === PreviewItemType.AUTOMATION &&
          t(`This automation won't run for this audience`)}
      </TypographyGray>
    </EmptyStateContainer>
  );
};

const PreviewItemDetailsPreviewAutomation = ({
  item,
  org,
  taskBuilderContext,
  anchorFields,
  actionDefinitions,
  tokens,
  selectedAudience
}: {
  item: PreviewItemAutomation;
  org: IOrganizationDenormalized;
  taskBuilderContext: MinimalTaskBuilderContext;
  anchorFields: AnchorField[];
  actionDefinitions: ActionDefinition[];
  tokens: TokenDefinition[];
  selectedAudience: Audience | null;
}) => {
  const actions = item.item.actions.filter((ac) =>
    selectedAudience?.blocks.includes(ac.templateId)
  );

  const isEmpty = actions.length <= 0;

  if (isEmpty) {
    return <EmptyContent type={item.type} />;
  }

  return (
    <div>
      {actions.map((a) => {
        const def = actionDefinitions.find((ad) => ad.type === a.type);
        if (!def) {
          return null;
        }
        return (
          <ActionSummaryContainer key={a.id}>
            <ActionSummary
              a={a}
              expTpl={taskBuilderContext}
              def={def}
              anchorFields={anchorFields}
            />
          </ActionSummaryContainer>
        );
      })}
    </div>
  );
};

const PreviewItemDetailsPreviewMessage = ({
  item,
  org,
  taskBuilderContext,
  anchorFields,
  tokens,
  selectedAudience
}: {
  item: PreviewItemMessage;
  org: IOrganizationDenormalized;
  taskBuilderContext: MinimalTaskBuilderContext;
  anchorFields: AnchorField[];
  tokens: TokenDefinition[];
  selectedAudience: Audience | null;
}) => {
  const message: IExperienceMessageDenormalized =
    selectedAudience === null
      ? item.item
      : {
          ...item.item,
          blocks: (item.item.blocks ?? []).filter((b) => {
            const block = b as any;
            const templateId = block['templateId'];
            const id = templateId || b.id;

            return selectedAudience.blocks.includes(id);
          })
        };
  const isEmpty = message.blocks.length <= 0;

  return (
    <>
      {isEmpty ? (
        <EmptyContent type={item.type} />
      ) : (
        <ExperienceMessagePreviewBody
          denormalizedMessage={message}
          denormalizedOrg={org}
          infoBox={null}
          wontSend={false}
          emptyState={null}
          selectedBlock={null}
          anchorFields={anchorFields}
          tokens={tokens}
          validation={EMPTY_OBJ}
          draggable={false}
          expTpl={taskBuilderContext}
          useSlackHeader={false}
        />
      )}
    </>
  );
};

const PreviewItemDetailsPreviewTask = ({
  item,
  org,
  taskBuilderContext,
  anchorFields,
  tokens,
  selectedAudience
}: {
  item: PreviewItemTask;
  org: IOrganizationDenormalized;
  taskBuilderContext: MinimalTaskBuilderContext;
  anchorFields: AnchorField[];
  tokens: TokenDefinition[];
  selectedAudience: Audience | null;
}) => {
  const task: ITaskDenormalized =
    selectedAudience === null
      ? item.item
      : {
          ...item.item,
          blocks: (item.item.blocks ?? []).filter((b) => {
            const block = b as any;
            const templateId = block['templateId'];
            const id = templateId || b.id;

            return selectedAudience.blocks.includes(id);
          })
        };
  const isEmpty = task.blocks.length <= 0;
  return (
    <>
      {isEmpty ? (
        <EmptyContent type={item.type} />
      ) : (
        <TaskMessagePreviewStart
          task={task}
          actor={item.item.actor}
          tokens={tokens}
          anchorFields={anchorFields}
          draggable={false}
          validation={EMPTY_OBJ}
          selectedBlock={null}
          org={org}
          expTpl={taskBuilderContext}
          batchMode={BatchMode.DEFAULT /* TODO !!! */}
        />
      )}
    </>
  );
};

const AudienceBlock = ({
  value,
  onPreview,
  selected,
  customUserFields,
  hasOtherAudiences,
  tz
}: {
  value: Audience;
  onPreview: (audience: Audience) => void;
  selected: boolean;
  customUserFields: CustomUserField[];
  hasOtherAudiences: boolean;
  tz: string;
}) => {
  const { t } = useUnsafeTranslation();
  const config = useUserRuleConfigSimple(customUserFields, tz, {
    // just used for rendering - no special filtering required
    orgId: null,
    experienceTemplateId: null
  });

  return (
    <AudienceBlockContainer
      noMargin
      role="button"
      onClick={() => {
        onPreview(value);
      }}
      selected={selected}
    >
      <AudienceBlockTitle>
        <WithShape
          wrap
          shape="square"
          color={value.color || DEFAULT_AUDIENCE_COLOR}
        >
          <Typography variant="h5">
            {getAudienceName(value, hasOtherAudiences, t)}
          </Typography>
        </WithShape>
      </AudienceBlockTitle>
      {selected && (
        <Typography variant="caption">
          {value.rule === 'EVERYONE' ? (
            <Typography variant="caption" component="p">
              {t(
                'All employees enrolled in this experience who don’t match the other Audiences'
              )}
            </Typography>
          ) : (
            <RuleRenderer
              variant="multiline"
              value={value.rule}
              config={config}
            />
          )}
        </Typography>
      )}
    </AudienceBlockContainer>
  );
};

const SideContainer = styled('div')((p) => ({
  padding: `${p.theme.spacing(4)}px ${p.theme.spacing(3)}px`,
  height: '100%',
  width: '100%',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column'
}));

// const SettingsSection = styled('div')((p) => ({
//   marginBottom: p.theme.spacing(3)
// }));

const SettingsSection = styled('div')<{
  divider?: boolean;
}>((p) => ({
  marginBottom: p.theme.spacing(3),
  paddingBottom: p.divider ? p.theme.spacing(3) : undefined,
  borderBottom: p.divider ? p.theme.custom.border.standard : undefined,
  cursor: p.onClick ? 'pointer' : undefined
}));

const AudiencesSection = ({
  selectedAudience,
  onPreviewAudience,
  audiences,
  customFields,
  type
}: {
  selectedAudience: Audience | null;
  onPreviewAudience: (audience: Audience) => void;
  audiences: Audience[];
  customFields: CustomUserField[];
  type: PreviewItemType;
}) => {
  const { t } = useUnsafeTranslation();
  const emptyAudiences = audiences.filter((a) => a.blocks.length <= 0);
  const nonEmtyAudiences = audiences.filter((a) => a.blocks.length > 0);

  return (
    <SettingsSection divider={false}>
      <FlexContainer
        direction="column"
        alignItems="flex-start"
        spacing={3}
        fullWidth
      >
        <FlexContainer direction="column" alignItems="flex-start" spacing={3}>
          <FlexContainer
            direction="column"
            alignItems="flex-start"
            spacing={0.5}
          >
            <Typography component="h4" variant="h4">
              {t('Audiences')}
            </Typography>
            <Typography component="p" variant="caption">
              {t(
                'Target Audiences enable sending custom messages, tasks and automatons to different groups of people based on profile data.'
              )}
            </Typography>
          </FlexContainer>

          {nonEmtyAudiences.length > 0 && (
            <FlexContainer
              direction="column"
              alignItems="flex-start"
              spacing={2}
              fullWidth
            >
              <Typography variant="h5" component="h5">
                {type === PreviewItemType.MESSAGE &&
                  t(`Audiences who will receive this message`)}
                {type === PreviewItemType.TASK &&
                  t(`Audiences who will receive this task`)}
                {type === PreviewItemType.AUTOMATION &&
                  t(`Audiences for whom this automation will run`)}
              </Typography>
              {nonEmtyAudiences.map((a) => (
                <AudienceBlock
                  value={a}
                  selected={a.id === selectedAudience?.id}
                  onPreview={onPreviewAudience}
                  customUserFields={customFields}
                  hasOtherAudiences={audiences.length > 1}
                  tz="Europe/Berlin"
                />
              ))}
            </FlexContainer>
          )}
        </FlexContainer>

        {emptyAudiences.length > 0 && (
          <FlexContainer
            direction="column"
            alignItems="flex-start"
            spacing={2}
            fullWidth
          >
            <Typography variant="h5" component="h5">
              {type === PreviewItemType.MESSAGE &&
                t(`Audiences who won't receive this message`)}
              {type === PreviewItemType.TASK &&
                t(`Audiences who won't receive this task`)}
              {type === PreviewItemType.AUTOMATION &&
                t(`Audiences for whom this automation won't run`)}
            </Typography>
            {emptyAudiences.map((a) => (
              <AudienceBlock
                value={a}
                selected={a.id === selectedAudience?.id}
                onPreview={onPreviewAudience}
                customUserFields={customFields}
                hasOtherAudiences={audiences.length > 1}
                tz="Europe/Berlin"
              />
            ))}
          </FlexContainer>
        )}
      </FlexContainer>
    </SettingsSection>
  );
};

const SendDateSection = ({
  startMoment,
  startItervalText,
  title,
  endMoment,
  endItervalText,
  triggeredBy,
  triggeredDeadline
}: {
  startMoment: Moment;
  endMoment?: Moment;
  title: string;
  startItervalText: string;
  endItervalText?: string;
  triggeredBy: ITaskDenormalized[] | undefined;
  triggeredDeadline?: IItemTemplateTimingTriggeredTask['end'];
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <SettingsSection divider={true}>
      <FlexContainer direction="column" alignItems="flex-start" spacing={1}>
        <Typography component="h4" variant="h4">
          {title}
        </Typography>

        <FlexContainer direction="column" alignItems="flex-start" spacing={1}>
          {triggeredBy?.length ? (
            <>
              <Typography component="p" variant="body1">
                <LightMdToJsx>
                  {`${t('After completion of')}: ${t('**{{ title }}**', {
                    title: escapeReplacements(
                      triggeredBy.map((x) => getItemDisplayName(x)).join(' & ')
                    )
                  })}`}
                </LightMdToJsx>
              </Typography>
              {triggeredDeadline && (
                <Typography component="p" variant="body1">
                  {/* {t('Deadline')}:{' '}
                  {triggeredDeadline.type === 'fixedDuration'
                    ? t('{{ duration }} after start', {
                        duration: toWeekOrDayDuration(
                          triggeredDeadline.duration,
                          t
                        )
                      })
                    : t('{{ duration }} after end of triggering task', {
                        duration: toWeekOrDayDuration(
                          triggeredDeadline.duration,
                          t
                        )
                      })} */}
                  {t('{{ duration }} deadline', {
                    duration: toWeekOrDayDuration(
                      triggeredDeadline.duration,
                      t,
                      {
                        forceSingular: true
                      }
                    )
                  })}
                </Typography>
              )}
            </>
          ) : (
            <>
              <div>
                <Typography component="p" variant="body1">
                  {endMoment && 'Starts on'}{' '}
                  {`${startMoment.format(
                    'dddd, MMMM DD'
                  )} at ${startMoment.format('HH:mm A')}`}
                </Typography>
                <Typography component="p" variant="caption">
                  {startItervalText}
                </Typography>
              </div>

              {endMoment && (
                <div>
                  <Typography component="p" variant="body1">
                    {`Deadline is ${endMoment.format(
                      'dddd, MMMM DD'
                    )} at ${endMoment.format('HH:mm A')}`}
                  </Typography>
                  <Typography component="p" variant="caption">
                    {endItervalText}
                  </Typography>{' '}
                </div>
              )}
            </>
          )}
        </FlexContainer>
      </FlexContainer>
    </SettingsSection>
  );
};

const PreviewItemDetailsMetaMessage = ({
  item,
  audiences,
  tokens,
  customFields,
  selectedAudience,
  onPreviewAudience,
  intervalText,
  org,
  mode,
  triggeredBy
}: {
  item: PreviewItemMessage;
  audiences: Audience[];
  tokens: TokenDefinition[];
  customFields: CustomUserField[];
  selectedAudience: Audience | null;
  intervalText: string;
  org: IOrganizationDenormalized;
  onPreviewAudience: (audience: Audience) => void;
  mode: ExperiencePreviewMode;
  triggeredBy: ITaskDenormalized[] | undefined;
}) => {
  const moment = getMomentInTz(org.tz, item.item.sendAt.toMillis());
  return (
    <SideContainer>
      <SettingsBody>
        {item.item.adminName && (
          <SettingsSection divider={true}>
            <FlexContainer direction="column" alignItems="flex-start" spacing={1}>
              <Typography component="h4" variant="h4">
                Admin Name
              </Typography>
              <Typography>{item.item.adminName}</Typography>
            </FlexContainer>
          </SettingsSection>
        )}
        <SettingsSection divider={true}>
          <FlexContainer direction="column" alignItems="flex-start" spacing={1}>
            <Typography component="h4" variant="h4">
              Subject
            </Typography>
            <PlainTextWithReplacementPlaceholders
              text={item.item.subject}
              tokens={tokens}
            />
          </FlexContainer>
        </SettingsSection>
        <SendDateSection
          title="Start Date"
          startMoment={moment}
          startItervalText={intervalText}
          triggeredBy={triggeredBy}
        />

        {mode === ExperiencePreviewMode.EXPERIENCE && (
          <AudiencesSection
            audiences={audiences}
            onPreviewAudience={onPreviewAudience}
            selectedAudience={selectedAudience}
            customFields={customFields}
            type={item.type}
          />
        )}
      </SettingsBody>
    </SideContainer>
  );
};

const PreviewItemDetailsMetaTask = ({
  item,
  audiences,
  tokens,
  customFields,
  selectedAudience,
  onPreviewAudience,
  org,
  startIntervalText,
  endIntervalText,
  mode,
  triggeredBy
}: {
  item: PreviewItemTask;
  audiences: Audience[];
  tokens: TokenDefinition[];
  customFields: CustomUserField[];
  selectedAudience: Audience | null;
  onPreviewAudience: (audience: Audience) => void;
  org: IOrganizationDenormalized;
  startIntervalText: string;
  endIntervalText: string;
  mode: ExperiencePreviewMode;
  triggeredBy: ITaskDenormalized[] | undefined;
}) => {
  const moment = getMomentInTz(org.tz, item.item.start.toMillis());
  const endMoment = getMomentInTz(org.tz, item.item.end.toMillis());
  return (
    <SideContainer>
      <SettingsBody>
        {item.item.adminName && (
          <SettingsSection divider={true}>
            <FlexContainer direction="column" alignItems="flex-start" spacing={1}>
              <Typography component="h4" variant="h4">
                Admin Name
              </Typography>
              <Typography>{item.item.adminName}</Typography>
            </FlexContainer>
          </SettingsSection>
        )}
        <SettingsSection divider={true}>
          <FlexContainer direction="column" alignItems="flex-start" spacing={1}>
            <Typography component="h4" variant="h4">
              Title
            </Typography>
            <PlainTextWithReplacementPlaceholders
              text={item.item.title}
              tokens={tokens}
            />
          </FlexContainer>
        </SettingsSection>

        <SendDateSection
          title="Start and Deadline"
          startMoment={moment}
          endMoment={endMoment}
          endItervalText={endIntervalText}
          startItervalText={startIntervalText}
          triggeredBy={triggeredBy}
          triggeredDeadline={item.triggered?.deadline}
        />

        {mode === ExperiencePreviewMode.EXPERIENCE && (
          <AudiencesSection
            audiences={audiences}
            onPreviewAudience={onPreviewAudience}
            selectedAudience={selectedAudience}
            customFields={customFields}
            type={item.type}
          />
        )}
      </SettingsBody>
    </SideContainer>
  );
};

const PreviewItemDetailsMetaAutomation = ({
  item,
  audiences,
  tokens,
  customFields,
  selectedAudience,
  onPreviewAudience,
  org,
  intervalText,
  mode,
  triggeredBy
}: {
  item: PreviewItemAutomation;
  audiences: Audience[];
  tokens: TokenDefinition[];
  customFields: CustomUserField[];
  selectedAudience: Audience | null;
  onPreviewAudience: (audience: Audience) => void;
  org: IOrganizationDenormalized;
  intervalText: string;
  mode: ExperiencePreviewMode;
  triggeredBy: ITaskDenormalized[] | undefined;
}) => {
  if (item.item.timing.type !== 'ABSOLUTE') {
    return null;
  }
  const moment = getMomentInTz(org.tz, item.item.timing.start.toMillis());
  return (
    <SideContainer>
      <SettingsBody>
        <SettingsSection divider={true}>
          <FlexContainer direction="column" alignItems="flex-start" spacing={1}>
            <Typography component="h4" variant="h4">
              Admin Name
            </Typography>
            <PlainTextWithReplacementPlaceholders
              text={item.item.title}
              tokens={tokens}
            />
          </FlexContainer>
        </SettingsSection>
        <SendDateSection
          title="Start Date"
          startMoment={moment}
          startItervalText={intervalText}
          triggeredBy={triggeredBy}
        />

        {mode === ExperiencePreviewMode.EXPERIENCE && (
          <AudiencesSection
            audiences={audiences}
            onPreviewAudience={onPreviewAudience}
            selectedAudience={selectedAudience}
            customFields={customFields}
            type={item.type}
          />
        )}
      </SettingsBody>
    </SideContainer>
  );
};
const PreviewBody = styled('div')((p) => ({
  display: 'flex',
  flex: 1,
  overflow: 'auto'
}));

const getDeadlineIntervalText = (
  t: UnsafeTFunction,
  task: ITaskDenormalized,
  intervalData: IntervalData
) => {
  const durationInDays = Math.abs(
    toMoment(task.end).diff(toMoment(task.start), 'days')
  );

  return getAnchorIntervalText(t, {
    ...intervalData,
    intervalToAnchorInDays: intervalData.intervalToAnchorInDays + durationInDays
  });
};

const PreviewItemDialog = ({
  open,
  onClose,
  item,
  org,
  taskBuilderContext,
  anchorFields,
  audiences,
  tokens,
  customFields,
  intervalData,
  mode,
  triggeredBy
}: {
  open: boolean;
  onClose: () => void;
  item: PreviewItem;
  org: IOrganizationDenormalized;
  taskBuilderContext: MinimalTaskBuilderContext;
  anchorFields: AnchorField[];
  audiences: Audience[];
  tokens: TokenDefinition[];
  customFields: CustomUserField[];
  intervalData: IntervalData;
  mode: ExperiencePreviewMode;
  triggeredBy: ITaskDenormalized[] | undefined;
}) => {
  const { t } = useUnsafeTranslation();
  const actionDefinitions = useActionDefinitions();
  const [selectedAudience, setSelectedAudience] = useState<Audience | null>(
    audiences[0] || getEveryoneAudience(audiences)
  );
  const onPreviewAudience = (audience: Audience) => {
    setSelectedAudience(audience);
  };

  if (!open) {
    return null;
  }

  const intervalText = getAnchorIntervalText(t, intervalData);

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth={'lg'}
      fullWidth
      classes={{
        scrollPaper: css({
          '&&': {
            alignItems: 'flex-start'
          }
        })
      }}
    >
      <DialogActionsHeader
        onClose={onClose}
        left={
          <Typography variant="h4">
            {t(
              item.type === PreviewItemType.TASK
                ? 'Preview Task'
                : item.type === PreviewItemType.MESSAGE
                ? 'Preview Message'
                : item.type === PreviewItemType.AUTOMATION
                ? 'Preview Automation'
                : ''
            )}
          </Typography>
        }
      />
      <DialogContentWithoutPadding>
        <PreviewItemDialogBody>
          <div>
            {item.type === PreviewItemType.MESSAGE && (
              <PreviewItemDetailsMetaMessage
                item={item}
                audiences={audiences}
                tokens={tokens}
                customFields={customFields}
                selectedAudience={selectedAudience}
                onPreviewAudience={onPreviewAudience}
                intervalText={intervalText}
                org={org}
                mode={mode}
                triggeredBy={triggeredBy}
              />
            )}
            {item.type === PreviewItemType.TASK && (
              <PreviewItemDetailsMetaTask
                item={item}
                audiences={audiences}
                tokens={tokens}
                customFields={customFields}
                selectedAudience={selectedAudience}
                onPreviewAudience={onPreviewAudience}
                startIntervalText={intervalText}
                endIntervalText={getDeadlineIntervalText(
                  t,
                  item.item,
                  intervalData
                )}
                org={org}
                mode={mode}
                triggeredBy={triggeredBy}
              />
            )}

            {item.type === PreviewItemType.AUTOMATION && (
              <PreviewItemDetailsMetaAutomation
                item={item}
                audiences={audiences}
                tokens={tokens}
                customFields={customFields}
                selectedAudience={selectedAudience}
                onPreviewAudience={onPreviewAudience}
                intervalText={intervalText}
                org={org}
                mode={mode}
                triggeredBy={triggeredBy}
              />
            )}
          </div>

          <PreviewBody>
            <MainContainer padding={3}>
              <DragDropContext onDragEnd={NOOP}>
                {item.type === PreviewItemType.MESSAGE && (
                  <PreviewItemDetailsPreviewMessage
                    item={item}
                    org={org}
                    selectedAudience={selectedAudience}
                    taskBuilderContext={taskBuilderContext}
                    anchorFields={anchorFields}
                    tokens={tokens}
                  />
                )}
                {item.type === PreviewItemType.TASK && (
                  <PreviewItemDetailsPreviewTask
                    item={item}
                    selectedAudience={selectedAudience}
                    org={org}
                    taskBuilderContext={taskBuilderContext}
                    anchorFields={anchorFields}
                    tokens={tokens}
                  />
                )}
                {item.type === PreviewItemType.AUTOMATION && (
                  <PreviewItemDetailsPreviewAutomation
                    item={item}
                    org={org}
                    selectedAudience={selectedAudience}
                    actionDefinitions={actionDefinitions}
                    taskBuilderContext={taskBuilderContext}
                    anchorFields={anchorFields}
                    tokens={tokens}
                  />
                )}
              </DragDropContext>
            </MainContainer>
          </PreviewBody>
        </PreviewItemDialogBody>
      </DialogContentWithoutPadding>
    </Dialog>
  );
};

export const PreviewItemComponent = ({
  item,
  actorDescriptor,
  org,
  variant,
  taskBuilderContext,
  anchorFields,
  channels,
  tokens,
  customFields,
  ts,
  intervalData,
  mode,
  triggeredBy
}: {
  item: PreviewItem;
  actorDescriptor: ActorDescriptor | null;
  org: IOrganizationDenormalized;
  variant: PreviewVariant;
  taskBuilderContext: MinimalTaskBuilderContext;
  anchorFields: AnchorField[];
  channels: ScheduleChannel[];
  tokens: TokenDefinition[];
  customFields: CustomUserField[];
  ts: Timestamp;
  intervalData: IntervalData;
  mode: ExperiencePreviewMode;
  triggeredBy: ITaskDenormalized[] | undefined;
}) => {
  // Title row - different style based on variant
  // - icon
  // - title
  // - audience marker IF the EVERYONE audience is not present
  //
  // Item bodies based on type and based on variant

  const theme = useTheme();
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);

  const { audiences } = item;

  const audiencesToRenderOnItemLevel = useMemo(() => {
    if (!audiences.length) {
      return EMPTY_ARR;
    }
    const everyoneAudience = getEveryoneAudience(audiences);
    if (everyoneAudience.blocks.length) {
      return EMPTY_ARR;
    }

    const audiencesWithoutEveryone = audiences.filter(
      (a) => a.id !== everyoneAudience.id && a.blocks.length
    );

    return audiencesWithoutEveryone;
  }, [audiences]);

  const audienceIdsOnItemLevel = useMemo(() => {
    return new Set(audiencesToRenderOnItemLevel.map((a) => a.id));
  }, [audiencesToRenderOnItemLevel]);

  const audiencesByBlock = audiencesToAudiencesByBlock(item.audiences);

  const blocksToDisplay = useMemo(() => {
    if (item.type === PreviewItemType.TASK) {
      return (item.item.blocks ?? []).filter((b) => isSubTaskBlock(b));
    }
    if (item.type === PreviewItemType.MESSAGE) {
      return (item.item.blocks ?? [])
        .filter(
          (b) =>
            b.type === ContentType.CALLOUT ||
            b.type === ContentType.HEADING ||
            b.type === ContentType.TEXT
        )
        .slice(0, 1);
    }
    return EMPTY_ARR;
  }, [item]);

  return (
    <>
      <FlexContainer
        fullWidth
        direction="column"
        alignItems="flex-start"
        style={{ overflow: 'hidden', cursor: 'pointer' }}
        onClick={openDialog}
      >
        <PreviewItemComponentTitle
          icon={getItemIcon(item)}
          color={
            actorDescriptor?.color || theme.custom.colors.purple.w500 // an automations purple
          }
          title={getItemTitle(item)}
          variant={variant}
          audiences={audiencesToRenderOnItemLevel}
          mode={mode}
        />
        {item.type === PreviewItemType.MESSAGE && (
          <PreviewItemBlocksComponent
            blocks={blocksToDisplay}
            audiencesByBlock={audiencesByBlock}
            audienceIdsOnItemLevel={audienceIdsOnItemLevel}
            allAudiences={audiences}
            mode={mode}
          />
        )}

        {item.type === PreviewItemType.TASK && variant === 'normal' && (
          <PreviewItemBlocksComponent
            blocks={blocksToDisplay}
            audiencesByBlock={audiencesByBlock}
            audienceIdsOnItemLevel={audienceIdsOnItemLevel}
            allAudiences={audiences}
            mode={mode}
          />
        )}

        {item.type === PreviewItemType.AUTOMATION && variant === 'normal' && (
          <PreviewItemActionsComponent
            actions={item.item.actions}
            audiencesByBlock={audiencesByBlock}
            audienceIdsOnItemLevel={audienceIdsOnItemLevel}
            allAudiences={audiences}
            mode={mode}
          />
        )}
      </FlexContainer>
      <PreviewItemDialog
        open={dialogOpen}
        onClose={closeDialog}
        item={item}
        org={org}
        taskBuilderContext={taskBuilderContext}
        anchorFields={anchorFields}
        audiences={audiences}
        tokens={tokens}
        customFields={customFields}
        intervalData={intervalData}
        mode={mode}
        triggeredBy={triggeredBy}
      />
    </>
  );
};

const CollapsibleChannelContainer = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  gap: p.theme.spacing(0),
  height: '100%',

  '> :last-child': {
    visibility: 'hidden',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    width: 0
  },

  ':hover > :last-child': {
    visibility: 'visible',
    width: 'auto',
    marginLeft: p.theme.spacing(0.5)
  }
}));
const CollapsibleChannel = ({ channel }: { channel: ScheduleChannel }) => {
  const { t } = useUnsafeTranslation();
  return (
    <CollapsibleChannelContainer>
      <ChannelIcon size={16} channel={channel} />
      <span>
        {t('Sent to {{ channel }}', {
          channel: channelToString(channel, t)
        })}
      </span>
    </CollapsibleChannelContainer>
  );
};

const ActorAndChannelInformationContainer = styled('div')((p) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: p.theme.spacing(1),
  fontSize: '0.8725rem',
  lineHeight: '1.1650rem',
  color: p.theme.custom.colors.gray.w700
}));
const ActorAndChannelInformation = ({
  actorDescriptor,
  channels,
  variant
}: {
  actorDescriptor: ActorDescriptor | null;
  channels: ScheduleChannel[];
  variant: PreviewVariant;
}) => {
  const { t } = useUnsafeTranslation();
  if (!actorDescriptor) {
    return null;
  }
  return (
    <ActorAndChannelInformationContainer>
      <ColoredContainer color={actorDescriptor.color}>
        <LightMdToJsx component="div">
          {t('Sent to **{{ name }}** ', {
            name: actorDescriptor?.label
          })}
        </LightMdToJsx>
      </ColoredContainer>
      {!!channels.length && variant === 'normal' && (
        <ColoredContainer color={actorDescriptor.color}>
          <FlexContainer spacing={0.5}>
            {channels.map((c, index) => (
              <CollapsibleChannel key={`${channelToKey(c)}-${index}`} channel={c} />
            ))}
          </FlexContainer>
        </ColoredContainer>
      )}
    </ActorAndChannelInformationContainer>
  );
};

const ItemsContainer = styled('div')((p) => ({
  border: p.theme.custom.border.standard,
  borderRadius: p.theme.custom.border.radius(),
  width: '100%'
}));

const ItemContainer = styled('div')((p) => ({
  borderBottom: p.theme.custom.border.standard,
  width: '100%',
  padding: p.theme.spacing(2),

  ':last-child': {
    borderBottom: 'none'
  }
}));

type IntervalData = {
  anchorName: string | null;
  intervalToAnchorInDays: number;
  groupType: PreviewGroupType;
};
export const PreviewBatchComponent = ({
  batch,
  actorDescriptors,
  org,
  variant,
  taskBuilderContext,
  anchorFields,
  tokens,
  customFields,
  ts,
  intervalData,
  mode,
  triggeredBy
}: {
  batch: PreviewBatch;
  actorDescriptors: ActorDescriptors;
  org: IOrganizationDenormalized;
  variant: PreviewVariant;
  taskBuilderContext: MinimalTaskBuilderContext;
  anchorFields: AnchorField[];
  tokens: TokenDefinition[];
  customFields: CustomUserField[];
  ts: Timestamp;
  intervalData: IntervalData;
  mode: ExperiencePreviewMode;
  triggeredBy: ITaskDenormalized[] | undefined;
}) => {
  // if actor and channels, show these
  // then render all items
  const descriptor = actorDescriptors[batch.actorKey] || null;
  return (
    <FlexContainer direction="column" alignItems="start" fullWidth spacing={1}>
      {batch.actorKey !== 'AUTOMATION' && (
        <ActorAndChannelInformation
          actorDescriptor={descriptor}
          channels={batch.channels}
          variant={variant}
        />
      )}
      <ItemsContainer>
        {batch.items.map((item, i) => (
          <ItemContainer
            key={
              item.type === PreviewItemType.TASK
                ? `task-${item.item.id}`
                : item.type === PreviewItemType.AUTOMATION
                ? `automation-${item.item.id}`
                : item.type === PreviewItemType.MESSAGE
                ? `message-${item.item.templateId}`
                : i
            }
          >
            <PreviewItemComponent
              item={item}
              actorDescriptor={descriptor}
              org={org}
              variant={variant}
              anchorFields={anchorFields}
              taskBuilderContext={taskBuilderContext}
              channels={batch.channels}
              tokens={tokens}
              customFields={customFields}
              ts={ts}
              intervalData={intervalData}
              mode={mode}
              triggeredBy={triggeredBy}
            />
          </ItemContainer>
        ))}
      </ItemsContainer>
    </FlexContainer>
  );
};

const ExperienceEndsBadge = styled('span')((p) => ({
  display: 'inline-block',
  marginTop: p.theme.spacing(0.5),
  backgroundColor: p.theme.custom.colors.gray.w200,
  color: p.theme.custom.colors.gray.w500,
  borderRadius: p.theme.custom.border.radius(),
  padding: `${p.theme.spacing(0.5)}px ${p.theme.spacing(1)}px`
}));

const getGroupSubtitle = (
  group: PreviewGroup,
  anchorName: string | null,
  t: UnsafeTFunction
) => {
  const subTitle = getAnchorIntervalText(t, {
    intervalToAnchorInDays: group.intervalToAnchorInDays,
    anchorName,
    groupType: group.type
  });
  if (group.type === PreviewGroupType.END) {
    return (
      <div>
        <div>{subTitle}</div>
        <div>
          <ExperienceEndsBadge>{t('Experience ends')}</ExperienceEndsBadge>
        </div>
      </div>
    );
  }

  return subTitle;
};

const CIRCLE_SIZE = 40;

const PreviewGroupSubTitleContainer = styled('div')<{
  invisibleBorder?: boolean;
}>((p) => ({
  width: '100%',
  marginLeft: CIRCLE_SIZE / 2,
  paddingLeft: CIRCLE_SIZE / 2 + p.theme.spacing(1),
  paddingBottom: p.theme.spacing(2),
  marginTop: -p.theme.spacing(0.5),
  zIndex: -1,
  borderLeft: p.theme.custom.border.standard,
  borderColor: p.invisibleBorder
    ? p.theme.palette.background.default
    : undefined
}));

const PreviewGroupOuterContainer = styled('div')((p) => ({
  width: '100%'
}));

const PreviewGroupInnerContainer = styled('div')<{ invisibleBorder?: boolean }>(
  (p) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    marginLeft: CIRCLE_SIZE / 2,
    paddingLeft: CIRCLE_SIZE / 2 + p.theme.spacing(1),
    paddingBottom: p.theme.spacing(4),
    borderLeft: p.theme.custom.border.standard,
    borderColor: p.invisibleBorder
      ? p.theme.palette.background.default
      : undefined,
    gap: p.theme.spacing(3)
  })
);

export const PreviewGroupComponent = ({
  group,
  org,
  actorDescriptors,
  variant,
  isLast,
  anchorName,
  taskBuilderContext,
  anchorFields,
  tokens,
  customFields,
  mode
}: {
  group: PreviewGroup;
  org: IOrganizationDenormalized;
  actorDescriptors: ActorDescriptors;
  variant: PreviewVariant;
  isLast: boolean;
  anchorName: string | null;
  taskBuilderContext: MinimalTaskBuilderContext;
  anchorFields: AnchorField[];
  tokens: TokenDefinition[];
  customFields: CustomUserField[];
  mode: ExperiencePreviewMode;
}) => {
  const { t } = useUnsafeTranslation();
  const isTriggered = group.type === PreviewGroupType.TRIGGERED;
  const tsTitle = isTriggered
    ? `${t('After completion of')}: ${escapeReplacements(
        (group.triggeredBy || EMPTY_ARR).map((x) => getItemDisplayName(x)).join(' & ')
      )}`
    : toMoment(group.ts).format('dddd, MMMM DD');
  const tsSubTitle = isTriggered ? '' : getGroupSubtitle(group, anchorName, t);
  const theme = useTheme();

  const getGroupIcon = () => {
    // They can never be the first item in the list anyway, so it's find to short circuit.
    switch (group.type) {
      case PreviewGroupType.START:
        return <Rocket size={20} color={theme.custom.colors.gray.w500} />;
      case PreviewGroupType.ANCHOR:
        return <Flag size={20} color={theme.custom.colors.gray.white} />;
      case PreviewGroupType.END:
        return <Flag size={20} color={theme.custom.colors.gray.w500} />;
      case PreviewGroupType.NORMAL:
        return <Calendar size={20} color={theme.custom.colors.gray.w500} />;
      case PreviewGroupType.TRIGGERED:
        return <TimelineIcon size={20} color={theme.custom.colors.gray.w500} />;
    }
  };

  return (
    <PreviewGroupOuterContainer>
      <FlexContainer spacing={1} fullWidth>
        <Avatar
          style={{
            height: CIRCLE_SIZE + 1, // + 1 for border
            width: CIRCLE_SIZE + 1, // + 1 for border
            border: 2,
            borderColor: theme.palette.background.default,
            borderStyle: 'solid',
            backgroundColor:
              group.type === PreviewGroupType.ANCHOR
                ? theme.custom.colors.gray.w500
                : theme.custom.colors.gray.w200
          }}
        >
          {getGroupIcon()}
        </Avatar>
        <Typography variant="h5">{tsTitle}</Typography>
      </FlexContainer>
      <PreviewGroupSubTitleContainer invisibleBorder={isLast}>
        <Typography variant="caption">{tsSubTitle}</Typography>
      </PreviewGroupSubTitleContainer>
      <PreviewGroupInnerContainer invisibleBorder={isLast}>
        {group.batches.map((b, index) => (
          <PreviewBatchComponent
            key={previewBatchToKey(b, index)}
            batch={b}
            org={org}
            actorDescriptors={actorDescriptors}
            variant={variant}
            taskBuilderContext={taskBuilderContext}
            anchorFields={anchorFields}
            tokens={tokens}
            ts={group.ts}
            intervalData={{
              anchorName,
              intervalToAnchorInDays: group.intervalToAnchorInDays,
              groupType: group.type
            }}
            customFields={customFields}
            mode={mode}
            triggeredBy={group.triggeredBy}
          />
        ))}
      </PreviewGroupInnerContainer>
    </PreviewGroupOuterContainer>
  );
};

const SHOW_MINIMAL_VIEW_SWITCH = false;

export const PreviewFilters = ({
  value,
  onChange,
  actorDescriptors
}: {
  value: PreviewFilterState;
  onChange: (nextValue: PreviewFilterState) => void;
  actorDescriptors: ActorDescriptors;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <FlexContainer fullWidth justifyContent="flex-end">
      {SHOW_MINIMAL_VIEW_SWITCH && (
        <FormControlLabel
          control={
            <Switch color="primary" checked={value.variant === 'normal'} />
          }
          onClick={() =>
            onChange({
              ...value,
              variant: value.variant === 'normal' ? 'minimal' : 'normal'
            })
          }
          label={<Typography variant="caption">{t('Show details')}</Typography>}
        />
      )}

      <FilterByActorChip
        value={value.actorKeys}
        onChange={(actorKeys) => onChange({ ...value, actorKeys })}
        actorDescriptors={actorDescriptors}
      />

      <FilterByItemTypeChip
        value={value.itemTypes}
        onChange={(itemTypes) => onChange({ ...value, itemTypes })}
      />
    </FlexContainer>
  );
};
